import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { fr as dateFnsFr, enGB as dateFnsEnGB, es as dateFnsEs } from 'date-fns/locale';

import accountFr from './locales/fr/account.json';
import actionsFr from './locales/fr/actions.json';
import commonFr from './locales/fr/common.json';
import dashboardFr from './locales/fr/dashboard.json';
import devicesFr from './locales/fr/devices.json';
import foldersFr from './locales/fr/folders.json';
import helpFr from './locales/fr/help.json';
import permissionsFr from './locales/fr/permissions.json';
import proceduresFr from './locales/fr/procedures.json';
import publicFr from './locales/fr/public.json';
import searchFr from './locales/fr/search.json';
import subsidiaryFr from './locales/fr/subsidiary.json';
import tokensFr from './locales/fr/tokens.json';
import verifyemailFr from './locales/fr/verifyemail.json';
import welcomeFr from './locales/fr/welcome.json';
import accountEn from './locales/en/account.json';
import actionsEn from './locales/en/actions.json';
import commonEn from './locales/en/common.json';
import dashboardEn from './locales/en/dashboard.json';
import devicesEn from './locales/en/devices.json';
import foldersEn from './locales/en/folders.json';
import helpEn from './locales/en/help.json';
import permissionsEn from './locales/en/permissions.json';
import proceduresEn from './locales/en/procedures.json';
import publicEn from './locales/en/public.json';
import searchEn from './locales/en/search.json';
import subsidiaryEn from './locales/en/subsidiary.json';
import tokensEn from './locales/en/tokens.json';
import verifyemailEn from './locales/en/verifyemail.json';
import welcomeEn from './locales/en/welcome.json';
import accountEs from './locales/es/account.json';
import actionsEs from './locales/es/actions.json';
import commonEs from './locales/es/common.json';
import dashboardEs from './locales/es/dashboard.json';
import devicesEs from './locales/es/devices.json';
import foldersEs from './locales/es/folders.json';
import helpEs from './locales/es/help.json';
import permissionsEs from './locales/es/permissions.json';
import proceduresEs from './locales/es/procedures.json';
import publicEs from './locales/es/public.json';
import searchEs from './locales/es/search.json';
import subsidiaryEs from './locales/es/subsidiary.json';
import tokensEs from './locales/es/tokens.json';
import verifyemailEs from './locales/es/verifyemail.json';
import welcomeEs from './locales/es/welcome.json';

export const SUPPORTED_LOCALES = ['fr', 'en', 'es'];
export const DEFAULT_LOCALE = 'fr';
export const LOCALE_STORAGE_KEY = 'locale';

/**
 * À qui appartient la langue stockée, écrit à côté d'elle :
 * - 'account' : elle relève de la session — langue du compte descendue par
 *               reconcileLocaleWithUser(), ou choix fait dans le sélecteur en
 *               étant connecté ;
 * - 'device'  : elle relève de ce navigateur seul — choix fait sans session
 *               (site public), ou lien ?lang=xx.
 *
 * L'écart décide de ce qui survit à la fin d'une session. Une langue 'account'
 * s'arrête avec elle, sinon le poste reste dans la langue du dernier connecté :
 * c'est ce qu'on voyait après une expiration silencieuse — onglet fermé,
 * session tombée côté serveur, aucun code n'ayant tourné pour faire le ménage.
 * Une langue 'device' n'a, elle, jamais rien à voir avec un compte et reste.
 *
 * Clé absente (valeurs écrites avant son introduction) = 'device' : mieux vaut
 * garder une langue de trop que retraduire un poste sans raison.
 */
export const LOCALE_SOURCE_STORAGE_KEY = 'locale-source';

/** Efface la langue stockée et sa provenance. */
function clearStoredLocale() {
    try {
        localStorage.removeItem(LOCALE_STORAGE_KEY);
        localStorage.removeItem(LOCALE_SOURCE_STORAGE_KEY);
    } catch (e) {}
}

/**
 * Oublie la langue stockée si elle relevait de la session, et rebascule
 * l'affichage à chaud sur ce qui reste (?lang=xx, puis navigateur, puis fr) ;
 * laisse intact un choix qui n'appartient qu'à ce navigateur.
 *
 * À appeler à chaque fin de session — déconnexion explicite, 401 en cours de
 * route, jeton expiré au chargement. Le cas qu'aucun de ces appels ne couvre,
 * la session tombée pendant que l'onglet était fermé, est rattrapé au
 * démarrage par resolveInitialLocale().
 *
 * Vider le stockage ne suffit pas : la langue du compte est déjà chargée dans
 * i18next. Sans bascule, la page de connexion vers laquelle RequireAuth
 * redirige juste après restait dans la langue du compte jusqu'au prochain
 * rechargement — c'est ce qu'on voyait quand le serveur croyait encore la
 * session valide (window.__AUTH_STATE__) alors que GET /user répondait 401.
 */
export function forgetSessionLocale() {
    if (readStoredLocale()?.source !== 'account') return;
    clearStoredLocale();
    i18n.changeLanguage(resolveInitialLocale());
}

/**
 * Écrit la langue et sa provenance : c'est ce couple que relira
 * resolveInitialLocale() au prochain chargement.
 *
 * @param {string} locale
 * @param {'device'|'account'} source
 */
export function writeStoredLocale(locale, source) {
    try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        localStorage.setItem(LOCALE_SOURCE_STORAGE_KEY, source);
    } catch (e) {}
}

/**
 * Langue stockée sur cet appareil et sa provenance, ou null.
 *
 * Ne pas confondre avec i18n.language, qui vaut toujours quelque chose : une
 * langue déduite du navigateur n'est pas un choix, et ne doit pas être remontée
 * au serveur comme si l'utilisateur l'avait sélectionnée.
 *
 * @returns {{ locale: string, source: 'device'|'account' }|null}
 */
export function readStoredLocale() {
    try {
        const locale = localStorage.getItem(LOCALE_STORAGE_KEY);
        if (!locale || !SUPPORTED_LOCALES.includes(locale)) return null;
        const source = localStorage.getItem(LOCALE_SOURCE_STORAGE_KEY);
        return { locale, source: source === 'account' ? 'account' : 'device' };
    } catch (e) {
        return null;
    }
}

/**
 * Vrai si le serveur affirme qu'il n'y a pas de session, via le
 * window.__AUTH_STATE__ injecté par app.blade.php — script classique placé
 * dans le body, donc exécuté avant ce module, chargé lui en `type=module`.
 *
 * `=== false` et non `!…` : global absent, c'est qu'on ne sait pas, et dans le
 * doute on ne jette pas la langue stockée.
 */
function hasNoServerSession() {
    return window.__AUTH_STATE__?.isAuthenticated === false;
}

/**
 * Résout la langue initiale, par ordre de priorité :
 * 1. Query param ?lang=xx (persisté en localStorage — pratique pour tester ou partager un lien)
 * 2. localStorage, si la valeur a encore cours : une langue de compte est
 *    abandonnée dès qu'il n'y a plus de session (cf. LOCALE_SOURCE_STORAGE_KEY)
 * 3. Langue du navigateur (navigator.language), si supportée
 * 4. Fallback : fr
 *
 * Même ordre de priorité que resolveThemeSlug() dans theme-context.jsx.
 */
function resolveInitialLocale() {
    const params = new URLSearchParams(window.location.search);
    const paramLocale = params.get('lang');
    if (paramLocale && SUPPORTED_LOCALES.includes(paramLocale)) {
        writeStoredLocale(paramLocale, 'device');
        return paramLocale;
    }

    const stored = readStoredLocale();
    if (stored) {
        if (stored.source === 'account' && hasNoServerSession()) {
            // La session est finie : la langue du compte s'arrête avec elle.
            clearStoredLocale();
        } else {
            return stored.locale;
        }
    }

    const browserLocale = (navigator.language || '').split('-')[0];
    if (SUPPORTED_LOCALES.includes(browserLocale)) {
        return browserLocale;
    }

    return DEFAULT_LOCALE;
}

i18n.use(initReactI18next).init({
    resources: {
        fr: { account: accountFr, actions: actionsFr, common: commonFr, dashboard: dashboardFr, devices: devicesFr, folders: foldersFr, help: helpFr, permissions: permissionsFr, procedures: proceduresFr, public: publicFr, search: searchFr, subsidiary: subsidiaryFr, tokens: tokensFr, verifyemail: verifyemailFr, welcome: welcomeFr },
        en: { account: accountEn, actions: actionsEn, common: commonEn, dashboard: dashboardEn, devices: devicesEn, folders: foldersEn, help: helpEn, permissions: permissionsEn, procedures: proceduresEn, public: publicEn, search: searchEn, subsidiary: subsidiaryEn, tokens: tokensEn, verifyemail: verifyemailEn, welcome: welcomeEn },
        es: { account: accountEs, actions: actionsEs, common: commonEs, dashboard: dashboardEs, devices: devicesEs, folders: foldersEs, help: helpEs, permissions: permissionsEs, procedures: proceduresEs, public: publicEs, search: searchEs, subsidiary: subsidiaryEs, tokens: tokensEs, verifyemail: verifyemailEs, welcome: welcomeEs },
    },
    lng: resolveInitialLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: 'common',
    ns: ["account","actions","common","dashboard","devices","folders","help","permissions","procedures","public","search","subsidiary","tokens","verifyemail","welcome"],
    interpolation: {
        escapeValue: false, // React échappe déjà le contenu, pas besoin d'un second échappement
    },
    returnEmptyString: false,
});

/**
 * Tag BCP 47 pour Intl / toLocaleDateString, par locale applicative.
 * en-GB (pas en-US) : le produit suit l'anglais britannique (cf. resources/lang
 * côté API — "Unauthorised", "finalise"…), y compris pour le format de date.
 */
export const INTL_LOCALE_TAGS = {
    fr: 'fr-FR',
    en: 'en-GB',
    es: 'es-ES',
};

/** @returns le tag BCP 47 (ex. "fr-FR") pour la locale i18next actuellement active */
export function getIntlLocaleTag() {
    return INTL_LOCALE_TAGS[i18n.language] ?? INTL_LOCALE_TAGS[DEFAULT_LOCALE];
}

const DATE_FNS_LOCALES = {
    fr: dateFnsFr,
    en: dateFnsEnGB,
    es: dateFnsEs,
};

/** @returns l'objet locale date-fns (pour `format(date, fmt, { locale })`) correspondant à la locale active */
export function getDateFnsLocale() {
    return DATE_FNS_LOCALES[i18n.language] ?? DATE_FNS_LOCALES[DEFAULT_LOCALE];
}

export default i18n;
